import type {BaseFetchResultsInterface} from '@interfaces/base'
import type {
  Conversation,
  CreateConversationArgs,
  StopConversationRunResult,
  UpdateConversationArgs,
} from '@interfaces/alpinist/conversation'
import type {CreateMessageArgs, Message, MessageAttachment} from '@interfaces/alpinist/message'
import type {PageContextV1} from '@shared/alpinist/pageContext/types'
import type {ConfirmMemoryArgs, ConfirmMemoryResult} from '@interfaces/alpinist/memoryConfirmation'
import type {
  AnswerMemoryItemArgs,
  AnswerMemoryItemResult,
  MemoryInventoryFilters,
  MemoryInventoryItem,
  TenantMemorySettings,
} from '@interfaces/alpinist/memorySettings'
import type {FetchBaseQueryMeta} from '@reduxjs/toolkit/query'
import {createApi} from '@reduxjs/toolkit/query/react'
import type {
  AlpinistUsageSummaryInterface,
  UsagePeriodDays,
} from '@interfaces/usage/organizationSummary'
import type {
  ScheduledPrompt,
  ScheduledPromptPatchPayload,
  ScheduledPromptUpsertPayload,
} from '@interfaces/alpinist/scheduledPrompt'
import type {
  CreateDocumentArgs,
  Document,
  DocumentFormats,
  DocumentSchema,
  DocumentUnit,
  DocumentVisibility,
  KbStats,
} from '@interfaces/alpinist/document'
import type {
  Integration,
  IntegrationUpsertPayload,
  SharePointConnection,
  SharePointSiteAccess,
  SharePointGrantRole,
  SharePointSiteGrantInstructions,
} from '@interfaces/alpinist/integration'
import type {
  MCPServer,
  MCPServerProbeResult,
  MCPServerUpsertPayload,
} from '@interfaces/alpinist/mcpServer'
import type {Skill, SkillSharePayload, SkillUpsertPayload} from '@interfaces/alpinist/skill'
import {createMontBlancBaseQuery} from '@apis/helpers.ts'
import type {
  DeleteMessageFeedbackArgs,
  MessageFeedback,
  UpsertMessageFeedbackArgs,
} from '@app/shared/interfaces/alpinist/messageFeedback'

interface PaginationSearchPromptsArgs {
  limit?: number
  offset?: number
  search?: string
  ordering?: string
  // List filters (see ScheduledPrompts UI). Omitted params are dropped by
  // fetchBaseQuery, so leaving these undefined keeps the unfiltered list.
  is_active?: boolean
  user_email?: string
}

// The sender's IANA zone, e.g. 'Europe/Berlin'. Sent with every chat message so
// Alpinist resolves "today" and "yesterday's shift" against the plant's clock
// instead of UTC (AB#5875).
//
// It comes from the user's PROFILE (`useCurrentUser().profileTimezone`), not from
// `Intl.DateTimeFormat()` at send time. The profile is what the user can see and
// correct in Settings and it follows them across devices, and DashTemplate already
// seeds it from the browser on first load — so reading the browser here as well
// would only add a second, invisible source that can silently disagree with the one
// the user was shown. Omitted when the profile has no zone; the backend then falls
// back to AGENT_DEFAULT_TIMEZONE, so a missing zone costs a less precise answer,
// never the message.
type CreateMessageBody = Omit<CreateMessageArgs, 'conversation_id'>

const buildCreateMessageBody = ({
  text,
  attachment_ids,
  timezone,
  page_context,
}: CreateMessageBody): CreateMessageBody => ({
  text,
  ...(attachment_ids?.length ? {attachment_ids} : {}),
  ...(timezone ? {timezone} : {}),
  ...(page_context ? {page_context} : {}),
})

// Matches the backend LimitOffsetPagination PAGE_SIZE.
const CONVERSATIONS_PAGE_SIZE = 25

export const aiCoreAPI = createApi({
  reducerPath: 'aiCoreAPI',
  baseQuery: createMontBlancBaseQuery('aicore'),
  tagTypes: [
    'Message',
    'MessageFeedback',
    'MemoryConfirmation',
    'Conversation',
    'TenantOptIn',
    'ScheduledPrompt',
    'ScheduledPromptRun',
    'Document',
    'KbStats',
    'Integration',
    'MCPServer',
    'SharePointConnection',
    'Skill',
    'MemoryItem',
  ],
  endpoints: (builder) => ({
    // --- Conversations ---
    listConversations: builder.query<
      Conversation[],
      {category?: string; incident_id?: string} | void
    >({
      query: (params): {url: string; params?: {category?: string; incident_id?: string}} => ({
        url: '/conversations/',
        params: params || undefined,
      }),
      transformResponse: (response: BaseFetchResultsInterface<Conversation>): Conversation[] =>
        response.results,
      providesTags: ['Conversation'],
    }),

    // Paginated + searchable conversation list for the switcher (infinite scroll).
    // `category` (general/scheduled mix) and `search` (title) are server-side, so
    // changing either resets to page 1 automatically (new cache entry).
    listConversationsInfinite: builder.infiniteQuery<
      BaseFetchResultsInterface<Conversation>,
      {category?: string; search?: string; archived?: boolean},
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) =>
          allPages.reduce((n, p) => n + p.results.length, 0) < lastPage.count
            ? lastPageParam + CONVERSATIONS_PAGE_SIZE
            : undefined,
      },
      query: ({queryArg, pageParam}) => ({
        url: '/conversations/',
        params: {
          category: queryArg.category,
          search: queryArg.search || undefined,
          // Only sent when viewing archived; otherwise the backend returns active.
          archived: queryArg.archived || undefined,
          limit: CONVERSATIONS_PAGE_SIZE,
          offset: pageParam,
        },
      }),
      providesTags: ['Conversation'],
    }),

    createConversation: builder.mutation<Conversation, CreateConversationArgs>({
      query: (body): {url: string; method: string; body: CreateConversationArgs} => ({
        url: '/conversations/create/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Conversation'],
    }),

    // PATCH user-editable conversation fields (e.g. a manual rename, which the
    // backend also marks title_locked so auto-summarization stops overwriting it).
    updateConversation: builder.mutation<Conversation, UpdateConversationArgs>({
      query: ({id, ...patch}) => ({
        url: `/conversations/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Conversation'],
    }),

    // One conversation, for whether a run holds it right now (AB#6760). The
    // socket events that drive the Stop button are missed across a load, a
    // reload or a reconnect, so the state is read back from here.
    getConversation: builder.query<Conversation, string>({
      query: (id) => ({url: `/conversations/${id}/`}),
      providesTags: (_result, _error, id) => [{type: 'Conversation', id}],
    }),

    // Stop the run on this conversation (AB#6760). Messages still waiting are
    // withdrawn; their texts come back over the socket, not in this response.
    // `soft` asks Alpinist to wrap up with an interim answer; `hard` cancels.
    stopConversationRun: builder.mutation<
      StopConversationRunResult,
      {conversation_id: string; mode: 'soft' | 'hard'}
    >({
      query: ({conversation_id, mode}) => ({
        url: `/conversations/${conversation_id}/stop/`,
        method: 'POST',
        body: {mode},
      }),
    }),

    // --- Messages ---
    listMessages: builder.query<
      BaseFetchResultsInterface<Message>,
      PaginationSearchPromptsArgs & {conversationId: string}
    >({
      query: ({conversationId, limit = 25, offset = 0}) => ({
        url: `/conversations/${conversationId}/messages/`,
        params: {
          limit,
          offset,
        },
      }),
      providesTags: ['Message'],
    }),

    createMessage: builder.mutation<Message, CreateMessageArgs>({
      query: ({
        conversation_id,
        ...body
      }): {
        url: string
        method: string
        body: CreateMessageBody
      } => ({
        url: `/conversations/${conversation_id}/messages/create/`,
        method: 'POST',
        // attachment_ids, timezone and page_context only when present, keeping
        // the plain-text payload unchanged.
        body: buildCreateMessageBody(body),
      }),
    }),

    // --- Message attachments (images and spreadsheets) ---
    createMessageAttachment: builder.mutation<
      MessageAttachment,
      {conversation_id: string; file: File}
    >({
      query: ({conversation_id, file}) => {
        const form = new FormData()
        form.append('file', file)
        return {
          url: `/conversations/${conversation_id}/attachments/create/`,
          method: 'POST',
          body: form,
        }
      },
    }),

    // --- look_at_screen (AB#6762): the open page's answer to a capture request ---
    // Multipart: the image, the visible text and the page context at capture
    // time. The first upload for a request wins; a later one is refused (409).
    uploadScreenCapture: builder.mutation<
      void,
      {
        conversation_id: string
        request_id: string
        image: Blob
        text: string
        page_context?: PageContextV1
      }
    >({
      query: ({conversation_id, request_id, image, text, page_context}) => {
        const form = new FormData()
        const extension = image.type === 'image/webp' ? 'webp' : 'jpg'
        form.append('image', image, `screen.${extension}`)
        form.append('text', text)
        if (page_context) form.append('page_context', JSON.stringify(page_context))
        return {
          url: `/conversations/${conversation_id}/screen-captures/${request_id}/`,
          method: 'POST',
          body: form,
        }
      },
    }),

    // The browser could not capture (AB#6762): the backend logs the reason and
    // Alpinist hears it, instead of a bare "screen not available" after 15 s.
    reportScreenCaptureFailure: builder.mutation<
      void,
      {conversation_id: string; request_id: string; error: string}
    >({
      query: ({conversation_id, request_id, error}) => ({
        url: `/conversations/${conversation_id}/screen-captures/${request_id}/`,
        method: 'POST',
        body: {error},
      }),
    }),

    previewMessageAttachment: builder.query<Blob, {conversation_id: string; attachment_id: string}>(
      {
        query: ({conversation_id, attachment_id}) => ({
          url: `/conversations/${conversation_id}/attachments/${attachment_id}/preview/`,
          method: 'GET',
          responseHandler: (response: Response): Promise<Blob> => response.blob(),
        }),
      },
    ),

    // --- Message feedback (me) ---
    upsertMessageFeedbackMe: builder.mutation<MessageFeedback, UpsertMessageFeedbackArgs>({
      query: ({conversationId, messageId, rating, details}) => ({
        url: `/conversations/${conversationId}/messages/${messageId}/feedback/me/`,
        method: 'PUT',
        body: {rating, details},
      }),
      invalidatesTags: (_res, _err, args) => [
        {type: 'MessageFeedback', id: args.messageId},
        'Message',
      ],
    }),

    // --- Memory confirmation (me) ---
    //
    // Deliberately the same URL family as message feedback: the same per-message
    // "me" gesture on a different subject. The server reads WHICH claim was asked
    // about from its own ask record, so this body carries only the verdict -- a
    // client able to name an item id could promote a claim nobody asked it about.
    confirmMemoryMe: builder.mutation<ConfirmMemoryResult, ConfirmMemoryArgs>({
      query: ({conversationId, messageId, verdict, comment}) => ({
        url: `/conversations/${conversationId}/messages/${messageId}/memory-confirmation/me/`,
        method: 'PUT',
        body: {verdict, comment},
      }),
      invalidatesTags: (_res, _err, args) => [
        {type: 'MemoryConfirmation', id: args.messageId},
        'Message',
      ],
    }),

    // --- Write notices (the card that says what was recorded) ---
    //
    // Scope travels both ways now. Everything `remember` writes is PERSONAL, so
    // `shareMemoryItem` is how a claim reaches colleagues at all -- the comment that
    // used to sit here argued the opposite, from a time when every write was
    // organization-scoped and a promote button would have been a second, easier path
    // to the same place. Sharing lands a claim at `proposed`, not `established`: a
    // colleague still has to agree, and any one of them can dispute it.
    dismissMemoryWrite: builder.mutation<{id: string; dismissed: boolean}, {writeId: string}>({
      query: ({writeId}) => ({
        url: `/memory/writes/${writeId}/dismiss/`,
        method: 'POST',
      }),
      invalidatesTags: ['Message'],
    }),

    // Answering a proposal is where the check on a claim actually happens now. The
    // gate used to do it by demanding a byte-exact slice of something the user had
    // typed, which proves the characters and not the claim — it refused five of the
    // first twelve production writes and caught no fabrication at all.
    approveMemoryProposal: builder.mutation<
      {id: string; outcome: string; item_id: string | null},
      {writeId: string; claim?: string}
    >({
      query: ({writeId, claim}) => ({
        url: `/memory/writes/${writeId}/approve/`,
        method: 'POST',
        // An edited claim is the strongest provenance available: whatever they type
        // is theirs by construction.
        body: claim ? {claim} : {},
      }),
      invalidatesTags: ['Message'],
    }),

    declineMemoryProposal: builder.mutation<{id: string; outcome: string}, {writeId: string}>({
      query: ({writeId}) => ({
        url: `/memory/writes/${writeId}/decline/`,
        method: 'POST',
      }),
      invalidatesTags: ['Message'],
    }),

    shareMemoryItem: builder.mutation<
      {id: string; scope: string; status: string; merged_into?: string},
      {itemId: string}
    >({
      query: ({itemId}) => ({
        url: `/memory/${itemId}/share/`,
        method: 'POST',
      }),
      // `MemoryItem` too: the claim leaves the personal inventory and joins the
      // organization one, so both lists are now wrong.
      invalidatesTags: ['Message', 'MemoryItem'],
    }),

    keepMemoryToMyself: builder.mutation<
      {id: string; scope: string; status: string},
      {itemId: string}
    >({
      query: ({itemId}) => ({
        url: `/memory/${itemId}/keep-to-myself/`,
        method: 'POST',
      }),
      invalidatesTags: ['Message'],
    }),

    deleteMessageFeedbackMe: builder.mutation<{ok?: boolean} | void, DeleteMessageFeedbackArgs>({
      query: ({conversationId, messageId}) => ({
        url: `/conversations/${conversationId}/messages/${messageId}/feedback/me/delete/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_res, _err, args) => [
        {type: 'MessageFeedback', id: args.messageId},
        'Message',
      ],
    }),

    // --- Flow opt-in check ---
    // Account management → Usage: organization-level Alpinist totals (AB#6637).
    getAlpinistUsageSummary: builder.query<AlpinistUsageSummaryInterface, UsagePeriodDays>({
      query: (periodDays) => ({
        url: `/usage/organization-summary/`,
        method: 'GET',
        // The viewer's zone sets the week boundaries of the weekly series.
        params: {period_days: periodDays, tz: Intl.DateTimeFormat().resolvedOptions().timeZone},
      }),
    }),

    checkTenantOptIn: builder.query<boolean, void>({
      query: (): {url: string; method: string} => ({
        url: `/tenant-opt-in/`,
        method: 'GET',
      }),
      transformResponse: (
        response: {
          is_opted_in: boolean
          detail?: string
        },
        _meta?: FetchBaseQueryMeta,
      ): boolean => {
        return Boolean(response?.is_opted_in)
      },
      providesTags: ['TenantOptIn'],
    }),
    setTenantOptIn: builder.mutation<
      {is_opted_in: boolean; detail?: string},
      {is_opted_in: boolean}
    >({
      query: (body) => ({url: `/tenant-opt-in/create/`, method: 'POST', body}),
      invalidatesTags: ['TenantOptIn'],
    }),

    // --- Alpinist web search toggle (tenant-level) ---
    getTenantWebSearch: builder.query<boolean, void>({
      query: () => ({url: `/tenant-opt-in/`, method: 'GET'}),
      transformResponse: (response: {web_search_enabled?: boolean}): boolean =>
        Boolean(response?.web_search_enabled),
      providesTags: ['TenantOptIn'],
    }),
    setTenantWebSearch: builder.mutation<
      {web_search_enabled: boolean},
      {web_search_enabled: boolean}
    >({
      query: (body) => ({url: `/tenant-opt-in/web-search/`, method: 'POST', body}),
      invalidatesTags: ['TenantOptIn'],
    }),

    // How Alpinist's memory behaves for this tenant. Read by anybody who may use
    // Alpinist, because the answers they get depend on it; admin-only to change,
    // like web search, because a change alters what everyone's answers are based on.
    getTenantMemorySettings: builder.query<TenantMemorySettings, void>({
      query: () => ({url: `/tenant-opt-in/memory-settings/`, method: 'GET'}),
      providesTags: ['TenantOptIn'],
    }),
    // Partial on purpose: the UI sends only the control that was touched, so two
    // administrators editing different settings cannot overwrite each other.
    setTenantMemorySettings: builder.mutation<TenantMemorySettings, Partial<TenantMemorySettings>>({
      query: (body) => ({
        url: `/tenant-opt-in/memory-settings/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['TenantOptIn'],
    }),

    // What Alpinist has learned about this organization, as a list somebody can read.
    // Scoped by the server to what THIS caller would actually be served, so the list
    // is a transparency surface and not a way around the read path's access control.
    listMemoryItems: builder.query<
      BaseFetchResultsInterface<MemoryInventoryItem>,
      MemoryInventoryFilters | void
    >({
      query: (params) => ({url: `/memory/`, method: 'GET', params: params || {}}),
      providesTags: ['MemoryItem'],
    }),
    // Takes a claim out of use. Any holder of `confirm_alpinist_knowledge` may:
    // stopping a wrong shared fact has to be cheaper than establishing a right one.
    // `Message` too: the write notice in the chat carries the claim's status, so
    // retracting from the card has to refresh the message list as well as the
    // inventory. Two endpoints for one URL would have silently overridden each other.
    retractMemoryItem: builder.mutation<{id: string; status: string}, {id: string}>({
      query: ({id}) => ({url: `/memory/${id}/retract/`, method: 'POST'}),
      invalidatesTags: ['MemoryItem', 'Message'],
    }),
    // Back to the QUEUE, not back to use: it returns as unconfirmed and has to earn
    // agreement again, or a claim one person judged wrong would come back with one
    // click and the two buttons would be a war whose winner clicked last.
    restoreMemoryItem: builder.mutation<{id: string; status: string}, {id: string}>({
      query: ({id}) => ({url: `/memory/${id}/restore/`, method: 'POST'}),
      invalidatesTags: ['MemoryItem'],
    }),
    // One person's answer about one organization claim, by its id. The per-message
    // `confirmMemoryMe` cannot carry this: it reads the claim from the ask the server
    // recorded, and a claim somebody found in the inventory was never asked about.
    // The server keeps one answer per person, so answering again replaces the earlier
    // one, and `unsure` withdraws an objection.
    // `Message` too: the same claim can be the subject of an ask under an answer, and
    // that prompt shows the claim's status.
    answerMemoryItem: builder.mutation<AnswerMemoryItemResult, AnswerMemoryItemArgs>({
      query: ({id, verdict, comment}) => ({
        url: `/memory/${id}/confirm/`,
        method: 'POST',
        body: comment ? {verdict, comment} : {verdict},
      }),
      invalidatesTags: ['MemoryItem', 'Message'],
    }),
    // Takes the caller's own answer away, leaving them as though they never answered.
    // Not the same as answering `unsure`: that is an answer, it reads as "you were not
    // sure" and takes the claim out of the reader's waiting list. An undo, or taking
    // back an objection that was the reader's only answer, has to land on nothing.
    withdrawMemoryAnswer: builder.mutation<AnswerMemoryItemResult, {id: string}>({
      query: ({id}) => ({url: `/memory/${id}/confirm/`, method: 'DELETE'}),
      invalidatesTags: ['MemoryItem', 'Message'],
    }),
    // Out of every list AND every answer, while the row and its revisions stay for
    // the audit trail. Distinct from retract, which keeps the claim listed with its
    // status so "what did we stop, and who stopped it" is still answerable.
    // Invalidates `Message` too: a write notice under an answer offers Remove, and
    // it has to stop offering it once the claim is gone.
    deleteMemoryItem: builder.mutation<{id: string; deleted: boolean}, {id: string}>({
      query: ({id}) => ({url: `/memory/${id}/delete/`, method: 'POST'}),
      invalidatesTags: ['MemoryItem', 'Message'],
    }),

    // ---- Scheduled Prompts ----
    listScheduledPrompts: builder.query<
      BaseFetchResultsInterface<ScheduledPrompt>,
      PaginationSearchPromptsArgs
    >({
      query: (params = {}) => ({
        url: 'scheduled-prompts/',
        params: {
          limit: params.limit,
          offset: params.offset,
          search: params.search,
          ordering: params.ordering,
          is_active: params.is_active,
          user_email: params.user_email,
        },
      }),
      providesTags: ['ScheduledPrompt'],
    }),

    createScheduledPrompt: builder.mutation<ScheduledPrompt, ScheduledPromptUpsertPayload>({
      query: (body) => ({url: '/scheduled-prompts/create/', method: 'POST', body}),
      invalidatesTags: ['ScheduledPrompt'],
    }),

    updateScheduledPrompt: builder.mutation<
      ScheduledPrompt,
      {
        id: string
        patch: ScheduledPromptPatchPayload
      }
    >({
      query: ({id, patch}) => ({
        url: `/scheduled-prompts/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['ScheduledPrompt'],
    }),

    deleteScheduledPrompt: builder.mutation<{ok: true}, {id: string}>({
      query: ({id}) => ({url: `/scheduled-prompts/${id}/`, method: 'DELETE'}),
      invalidatesTags: ['ScheduledPrompt'],
    }),

    runScheduledPromptNow: builder.mutation<{ok: boolean}, {id: string}>({
      query: ({id}) => ({
        url: `/scheduled-prompts/${id}/run-now/`,
        method: 'POST',
        body: {},
      }),
      // The run creates a scheduled-report conversation (asynchronously), so mark
      // the conversation list stale — it refetches next time the chat opens.
      invalidatesTags: ['Conversation'],
    }),

    // --- Knowledge Base Documents ---
    listDocuments: builder.query<
      BaseFetchResultsInterface<Document>,
      (PaginationSearchPromptsArgs & {visibility?: DocumentVisibility}) | void
    >({
      query: (params) => ({
        url: '/kb/documents/',
        params: params ?? {},
      }),
      providesTags: ['Document'],
    }),

    createDocument: builder.mutation<Document, CreateDocumentArgs>({
      query: ({title, file, visibility}) => {
        const form = new FormData()
        form.append('title', title)
        form.append('file', file)
        // Omitted means the organization library, which is the backend default.
        if (visibility) form.append('visibility', visibility)
        return {
          url: '/kb/documents/create/',
          method: 'POST',
          body: form,
        }
      },
      invalidatesTags: ['Document', 'KbStats'],
    }),

    // Moving a document between the organization library and the uploader's own
    // private set. A flag change, not a re-upload: the file, its chunks and its
    // embeddings already exist (AB#5987). Both directions, because sharing
    // without unsharing is a one-way door.
    // 'conversation' included: taking a saved chat attachment back out is the
    // undo for saving it, and the server accepts it for any document that came
    // from a chat.
    setDocumentVisibility: builder.mutation<Document, {id: string; visibility: DocumentVisibility}>(
      {
        query: ({id, visibility}) => ({
          url: `/kb/documents/${id}/visibility/`,
          method: 'POST',
          body: {visibility},
        }),
        // 'Message' as well: a chat attachment carries its document's scope, so
        // without this the chip goes on saying "in this conversation only" after
        // the file has been kept.
        invalidatesTags: ['Document', 'KbStats', 'Message'],
      },
    ),

    // The single source of truth for what can be uploaded (AB#5986). Cached
    // indefinitely — it changes when the backend ships, not while a tab is
    // open.
    getDocumentFormats: builder.query<DocumentFormats, void>({
      query: () => ({url: '/documents/formats/', method: 'GET'}),
      keepUnusedDataFor: 3600,
    }),

    deleteDocument: builder.mutation<void, {id: string}>({
      query: ({id}) => ({
        url: `/kb/documents/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Document', 'KbStats'],
    }),

    // One addressed unit of a document, as text — a sheet, a page, a section.
    // What a citation click actually wants: the passage the answer came from,
    // not the file (AB#5992). POST because the unit is named in a body, but it
    // is a read, so it is a query and caches like one.
    readDocumentUnit: builder.query<DocumentUnit, {id: string; number?: number; label?: string}>({
      query: ({id, number, label}) => ({
        url: `/kb/documents/${id}/read/`,
        method: 'POST',
        body: label ? {number, label} : {number},
      }),
    }),

    // Take back a file attached but not yet sent (AB#5992). Without it the
    // composer's remove button only dropped the chip: the upload it had already
    // made stayed behind, ingested and answering searches in that conversation.
    deleteMessageAttachment: builder.mutation<
      void,
      {conversation_id: string; attachment_id: string}
    >({
      query: ({conversation_id, attachment_id}) => ({
        url: `/conversations/${conversation_id}/attachments/${attachment_id}/`,
        method: 'DELETE',
      }),
    }),

    // The tables, columns and inferred joins of a queried document (AB#5993).
    // Shown when someone inspects a spreadsheet or a PLC export: its shape is
    // the informative thing about it, where a page of CSV is not.
    getDocumentSchema: builder.query<DocumentSchema, {id: string}>({
      query: ({id}) => ({
        url: `/kb/documents/${id}/schema/`,
        method: 'GET',
      }),
    }),

    previewDocument: builder.query<Blob, {id: string}>({
      query: ({id}) => ({
        url: `/kb/documents/${id}/preview/`,
        method: 'GET',
        responseHandler: (response: Response): Promise<Blob> => response.blob(),
      }),
    }),

    // Takes the same visibility filter as the list: a count beside a filtered
    // table has to agree with it (AB#5992).
    getKbStats: builder.query<KbStats, {visibility?: DocumentVisibility} | void>({
      query: (params) => ({
        url: '/kb/stats/',
        method: 'GET',
        params: params?.visibility ? {visibility: params.visibility} : undefined,
      }),
      providesTags: ['KbStats'],
    }),

    // --- Integrations ---
    listIntegrations: builder.query<
      BaseFetchResultsInterface<Integration>,
      PaginationSearchPromptsArgs
    >({
      query: (params = {}) => ({
        url: '/integrations/',
        method: 'GET',
        params: {
          limit: params.limit,
          offset: params.offset,
          search: params.search,
          ordering: params.ordering,
        },
      }),
      providesTags: ['Integration'],
    }),

    createIntegration: builder.mutation<Integration, IntegrationUpsertPayload>({
      query: (body) => ({
        url: '/integrations/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Integration'],
    }),

    updateIntegration: builder.mutation<
      Integration,
      {id: string; patch: Partial<IntegrationUpsertPayload>}
    >({
      query: ({id, patch}) => ({
        url: `/integrations/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Integration'],
    }),

    getIntegrationSecretValue: builder.query<
      {name: string; secret: string},
      {id: string; name: string}
    >({
      query: ({id, name}) => ({
        url: `/integrations/${id}/secret/`,
        method: 'GET',
        params: {name},
      }),
    }),

    deleteIntegration: builder.mutation<{ok?: boolean} | void, {id: string}>({
      query: ({id}) => ({
        url: `/integrations/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Integration'],
    }),

    // --- MCP servers (AB#5980) ---
    // An organization's own third-party MCP servers. Separate from Integrations:
    // an Integration configures our tools, an MCP server mounts somebody else's.
    listMCPServers: builder.query<
      BaseFetchResultsInterface<MCPServer>,
      PaginationSearchPromptsArgs
    >({
      query: (params = {}) => ({
        url: '/mcp-servers/',
        method: 'GET',
        params: {
          limit: params.limit,
          offset: params.offset,
          search: params.search,
          ordering: params.ordering,
        },
      }),
      providesTags: ['MCPServer'],
    }),

    createMCPServer: builder.mutation<MCPServer, MCPServerUpsertPayload>({
      query: (body) => ({
        url: '/mcp-servers/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MCPServer'],
    }),

    updateMCPServer: builder.mutation<
      MCPServer,
      {id: string; patch: Partial<MCPServerUpsertPayload>}
    >({
      query: ({id, patch}) => ({
        url: `/mcp-servers/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['MCPServer'],
    }),

    deleteMCPServer: builder.mutation<{ok?: boolean} | void, {id: string}>({
      query: ({id}) => ({
        url: `/mcp-servers/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MCPServer'],
    }),

    getMCPServerSecretValue: builder.query<{secret: string}, {id: string}>({
      query: ({id}) => ({
        url: `/mcp-servers/${id}/secret/`,
        method: 'GET',
      }),
    }),

    // Connects with the server's own stored token and lists what it offers, so
    // the tool allowlist is picked from the real catalogue rather than typed.
    probeMCPServer: builder.mutation<MCPServerProbeResult, {id: string}>({
      query: ({id}) => ({
        url: `/mcp-servers/${id}/probe/`,
        method: 'POST',
      }),
    }),

    // --- SharePoint Entra connection (AB#5847) ---
    // Tenant-wide, not per integration: one admin consent for the MontBlancAI
    // reader app, then one `Sites.Selected` grant per readable site.
    getSharePointConnection: builder.query<SharePointConnection, void>({
      query: () => ({
        url: '/integrations/sharepoint/connection/',
        method: 'GET',
      }),
      providesTags: ['SharePointConnection'],
    }),

    connectSharePoint: builder.mutation<SharePointConnection, {azure_tenant_id?: string} | void>({
      query: (body) => ({
        url: '/integrations/sharepoint/connection/',
        method: 'POST',
        body: body || {},
      }),
      invalidatesTags: ['SharePointConnection'],
    }),

    verifySharePointConnection: builder.mutation<SharePointConnection, void>({
      query: () => ({
        url: '/integrations/sharepoint/connection/verify/',
        method: 'POST',
      }),
      invalidatesTags: ['SharePointConnection'],
    }),

    // The prefilled grant request. No network call behind it, so it is
    // available the moment a site is pasted -- and stays available when the
    // tenant is unreachable.
    getSharePointSiteGrantInstructions: builder.mutation<
      SharePointSiteGrantInstructions,
      {site_url: string; roles?: SharePointGrantRole[]}
    >({
      query: (body) => ({
        url: '/integrations/sharepoint/connection/site-grants/instructions/',
        method: 'POST',
        body,
      }),
    }),

    checkSharePointSiteAccess: builder.mutation<SharePointSiteAccess, {site_url: string}>({
      query: (body) => ({
        url: '/integrations/sharepoint/connection/site-grants/check/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SharePointConnection'],
    }),

    forgetSharePointSiteGrant: builder.mutation<void, {grantId: string}>({
      query: ({grantId}) => ({
        url: `/integrations/sharepoint/connection/site-grants/${grantId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SharePointConnection'],
    }),

    // --- Skills ---
    listSkills: builder.query<
      BaseFetchResultsInterface<Skill>,
      PaginationSearchPromptsArgs & {scope?: string}
    >({
      query: (params = {}) => ({
        url: 'skills/',
        params: {
          limit: params.limit,
          offset: params.offset,
          search: params.search,
          ordering: params.ordering,
          scope: params.scope,
        },
      }),
      providesTags: ['Skill'],
    }),

    createSkill: builder.mutation<Skill, SkillUpsertPayload>({
      query: (body) => ({url: '/skills/create/', method: 'POST', body}),
      invalidatesTags: ['Skill'],
    }),

    updateSkill: builder.mutation<Skill, {id: string; patch: Partial<SkillUpsertPayload>}>({
      query: ({id, patch}) => ({url: `/skills/${id}/`, method: 'PATCH', body: patch}),
      invalidatesTags: ['Skill'],
    }),

    deleteSkill: builder.mutation<void, {id: string}>({
      query: ({id}) => ({url: `/skills/${id}/`, method: 'DELETE'}),
      invalidatesTags: ['Skill'],
    }),

    shareSkill: builder.mutation<Skill, {id: string} & SkillSharePayload>({
      query: ({id, ...body}) => ({url: `/skills/${id}/share/`, method: 'POST', body}),
      invalidatesTags: ['Skill'],
    }),

    setSkillEnabled: builder.mutation<Skill, {id: string; enabled: boolean}>({
      query: ({id, enabled}) => ({
        url: `/skills/${id}/set-enabled/`,
        method: 'POST',
        body: {enabled},
      }),
      invalidatesTags: ['Skill'],
    }),
  }),
})

export const {
  useGetAlpinistUsageSummaryQuery,
  useListConversationsQuery,
  useCreateConversationMutation,
  useUpdateConversationMutation,
  useGetConversationQuery,
  useStopConversationRunMutation,
  useListMessagesQuery,
  useLazyListMessagesQuery,
  useCreateMessageMutation,
  useCreateMessageAttachmentMutation,
  useUploadScreenCaptureMutation,
  useReportScreenCaptureFailureMutation,
  useDeleteMessageAttachmentMutation,
  usePreviewMessageAttachmentQuery,
  useLazyPreviewMessageAttachmentQuery,
  useUpsertMessageFeedbackMeMutation,
  useDeleteMessageFeedbackMeMutation,
  useConfirmMemoryMeMutation,
  useDismissMemoryWriteMutation,
  useApproveMemoryProposalMutation,
  useDeclineMemoryProposalMutation,
  useKeepMemoryToMyselfMutation,
  useCheckTenantOptInQuery,
  useSetTenantOptInMutation,
  useGetTenantWebSearchQuery,
  useSetTenantWebSearchMutation,
  useGetTenantMemorySettingsQuery,
  useSetTenantMemorySettingsMutation,
  useListMemoryItemsQuery,
  useRetractMemoryItemMutation,
  useRestoreMemoryItemMutation,
  useAnswerMemoryItemMutation,
  useWithdrawMemoryAnswerMutation,
  useDeleteMemoryItemMutation,
  useShareMemoryItemMutation,
  useListScheduledPromptsQuery,
  useLazyListScheduledPromptsQuery,
  useUpdateScheduledPromptMutation,
  useCreateScheduledPromptMutation,
  useDeleteScheduledPromptMutation,
  useRunScheduledPromptNowMutation,
  useListDocumentsQuery,
  useLazyListDocumentsQuery,
  useCreateDocumentMutation,
  useSetDocumentVisibilityMutation,
  useGetDocumentFormatsQuery,
  useDeleteDocumentMutation,
  usePreviewDocumentQuery,
  useLazyPreviewDocumentQuery,
  useReadDocumentUnitQuery,
  useGetDocumentSchemaQuery,
  useGetKbStatsQuery,
  useListIntegrationsQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useLazyGetIntegrationSecretValueQuery,
  useDeleteIntegrationMutation,
  useListMCPServersQuery,
  useCreateMCPServerMutation,
  useUpdateMCPServerMutation,
  useDeleteMCPServerMutation,
  useLazyGetMCPServerSecretValueQuery,
  useProbeMCPServerMutation,
  useGetSharePointConnectionQuery,
  useConnectSharePointMutation,
  useVerifySharePointConnectionMutation,
  useCheckSharePointSiteAccessMutation,
  useGetSharePointSiteGrantInstructionsMutation,
  useForgetSharePointSiteGrantMutation,
  useListSkillsQuery,
  useCreateSkillMutation,
  useUpdateSkillMutation,
  useDeleteSkillMutation,
  useShareSkillMutation,
  useSetSkillEnabledMutation,
} = aiCoreAPI
